import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { X } from 'lucide-react';
import {
  Modal, Table, InputNumber, Button, IconButton, Alert, Input, Typography, Segmented,
  Tooltip, CodeChip, entityHue, initialsOf, cn, message,
} from '../../ui';
import { request } from '../../api/client';
import { accentForProductName } from '../../lib/productAccent';
import { CopyValue } from '../../components/CopyValue';
import { fmtUom, normalizeMeasureLabel } from '../../lib/format';

/** A hue worn as a surface — wash, hairline ring, glyph. Inline styles need real colour values;
 *  Tailwind only emits the theme vars a CLASS references. */
const hueSkin = (hue: string, wash = 14, ring = 38) => ({
  background: `color-mix(in srgb, ${hue} ${wash}%, transparent)`,
  boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} ${ring}%, transparent)`,
  color: hue,
});

type Props = { warehouse: { id: string; code: string; name: string } | null; onClose: () => void };

type ProductRow = {
  productId: string; sku: string; name: string; groupCode: string | null; variantLabel: string | null;
  uom: string; packSize: number; level: number; isOverride: boolean;
};
type Resp = { warehouseId: string; warehouse: { id: string; code: string; name: string } | null; products: ProductRow[] };

/**
 * Warehouse-first minimum-stock editor: pick a warehouse, then set the minimum quantity to maintain
 * for each product variant in THAT warehouse. Writes the per-(product, warehouse) levels the
 * low-stock alerts check against.
 *
 * The number typed here is the WHOLE rule. There is no product-level default behind it: a blank cell
 * is 0, and 0 never raises an alert. Each warehouse states what it wants to hold, or states nothing.
 */
export function WarehouseReorderModal({ warehouse, onClose }: Props) {
  const qc = useQueryClient();
  const open = !!warehouse;
  const warehouseId = warehouse?.id ? String(warehouse.id) : '';
  // null = nothing set for this warehouse (counts as 0); a number = the level it wants to hold.
  const [edits, setEdits] = useState<Record<string, number | null>>({});
  const [search, setSearch] = useState('');
  // Which slice of the catalogue is on screen. Not a cosmetic filter: on a 30-row editor the three
  // products that actually carry a level are invisible among the twenty-seven that do not.
  const [lens, setLens] = useState<'all' | 'set' | 'unset'>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['wh-reorder', warehouseId],
    queryFn: () => request<Resp>({ url: `/products/reorder-by-warehouse/${warehouseId}` }),
    enabled: open && !!warehouseId,
  });

  useEffect(() => {
    if (data) {
      const init: Record<string, number | null> = {};
      for (const p of data.products) init[p.productId] = p.isOverride ? p.level : null;
      setEdits(init);
      setSearch('');
      setLens('all');
    }
  }, [data]);

  const isSet = (id: string) => edits[id] !== null && edits[id] !== undefined;

  const all = data?.products ?? [];
  // Counted off `edits`, so the tallies follow what is typed in the dialog rather than what was
  // loaded — clear a level and the strip says so before anything is saved.
  const setCount = all.filter((p) => isSet(p.productId)).length;
  const unsetCount = all.length - setCount;

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q))
      .filter((p) => (lens === 'all' ? true : lens === 'set' ? isSet(p.productId) : !isSet(p.productId)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, search, lens, edits]);

  const save = useConfirmedMutation({ title: "Save these minimum stock levels?", content: "Low-stock alerts are raised against these numbers.", okText: "Yes, save" }, {
    mutationFn: () => request({
      url: `/products/reorder-by-warehouse/${warehouseId}`,
      method: 'PUT',
      data: { items: (data?.products ?? []).map((p) => ({ productId: p.productId, level: edits[p.productId] ?? null })) },
    }),
    onSuccess: () => {
      message.success('Minimum stock levels saved');
      qc.invalidateQueries({ queryKey: ['wh-reorder', warehouseId] });
      qc.invalidateQueries({ queryKey: ['stock-reorder'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  return (
    <Modal
      open={open} onOpenChange={(o) => { if (!o) onClose(); }}
      title={warehouse ? `Minimum stock levels — ${warehouse.name} (${warehouse.code})` : 'Minimum stock levels'}
      width={760}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save levels</Button></>}
    >
      <Alert
        type="info" className="mb-3"
        title="Set the minimum quantity to maintain for each product variant in this warehouse."
        description="Low-stock alerts compare this warehouse's stock against these levels. A blank cell counts as 0 — no alert for that product here."
      />

      {/*
        WHAT THIS WAREHOUSE ACTUALLY HAS SET, said before the list.

        Every row looked identical whether or not it carried a level, so the handful of products this
        warehouse genuinely watches were invisible among the ones it does not — and "no minimum" is
        not a neutral blank here, it is the reason a product can run to zero without ever raising an
        alert. The counts are a PARTITION (set + none = all) and each one opens its own slice, so the
        strip is a lens on the list rather than a caption above it.
      */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search product / SKU" className="w-[260px]"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <Segmented
          className="ml-auto"
          value={lens} onChange={(v) => setLens(v as any)}
          options={[
            { value: 'all', label: `All ${all.length}` },
            { value: 'set', label: `With a minimum ${setCount}` },
            { value: 'unset', label: `No minimum ${unsetCount}` },
          ]}
        />
      </div>

      {unsetCount > 0 && lens !== 'unset' && (
        <button
          type="button" onClick={() => setLens('unset')}
          className="mb-3 flex w-full items-center gap-2 rounded-control border border-warning-500/30 bg-warning-500/10 px-3 py-2 text-left type-label text-warning-600 transition-colors hover:bg-warning-500/15 dark:text-harvest-300"
        >
          <span className="inline-block size-1.5 shrink-0 rounded-full bg-warning-500" />
          {unsetCount} of {all.length} product{all.length === 1 ? '' : 's'} have no minimum at {warehouse?.code} — no low-stock alert can ever fire for {unsetCount === 1 ? 'it' : 'them'} here.
          <span className="ml-auto shrink-0 font-semibold underline">Show {unsetCount === 1 ? 'it' : 'them'}</span>
        </button>
      )}
      <Table<ProductRow>
        rowKey="productId" size="small" loading={isLoading}
        dataSource={rows}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        locale={{ emptyText: lens === 'set' ? 'No product has a minimum set here yet' : lens === 'unset' ? 'Every product has a minimum here' : 'No active products' }}
        // A row that carries a level is a row this warehouse is actually watching. It says so.
        rowClassName={(p: ProductRow) => (isSet(p.productId) ? 'bg-brand-500/[0.06]' : '')}
        columns={[
          {
            title: 'Product', dataIndex: 'name',
            render: (v: string, p: ProductRow) => {
              // The product's own packaging colour (lib/productAccent) — the same hue it wears in the
              // stock table and the valuation chart, so a product is one colour wherever it is drawn.
              const accent = accentForProductName(v);
              // "0.5L" and "500 ml" are the same measure written twice — the variant chip already
              // said it. Only show the pack when it adds something the label does not.
              const pack = fmtUom(p.packSize, p.uom);
              const packIsNews = !!pack && normalizeMeasureLabel(pack) !== normalizeMeasureLabel(p.variantLabel);
              return (
                <span className="flex min-w-0 items-center gap-2.5">
                  <span className="grid size-8 shrink-0 place-items-center rounded-control type-caption-strong" style={hueSkin(accent, 16, 42)}>
                    {initialsOf(v)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-content">{v}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-1">
                      {p.variantLabel && (
                        <span className="rounded-pill px-1.5 py-px type-caption-strong leading-4" style={hueSkin(accent, 12, 30)}>
                          {p.variantLabel}
                        </span>
                      )}
                      {/* The pack was in the payload and shown nowhere — it is what tells you whether
                          a minimum of 20 is twenty bottles or twenty 20-kg sacks. */}
                      {packIsNews && <span className="type-caption text-subtle">{pack}</span>}
                    </span>
                  </span>
                </span>
              );
            },
          },
          {
            title: 'SKU', dataIndex: 'sku', width: 160,
            render: (v: string) => <CopyValue value={v} label="SKU"><CodeChip>{v}</CodeChip></CopyValue>,
          },
          {
            title: (
              <span className="inline-flex items-center gap-1.5">
                Minimum @
                {warehouse?.code ? <CodeChip hue={entityHue(warehouse.code)}>{warehouse.code}</CodeChip> : null}
              </span>
            ),
            width: 210,
            render: (_: any, p: ProductRow) => {
              const val = edits[p.productId];
              const on = val !== null && val !== undefined;
              return (
                <span className="flex items-center gap-1.5">
                  <InputNumber
                    min={0} step={1}
                    className={cn('w-[130px]', on && 'ring-1 ring-brand-500/45')}
                    value={val ?? undefined}
                    placeholder="0"
                    onChange={(x) => setEdits((m) => ({ ...m, [p.productId]: x === null || x === undefined ? null : Number(x) }))}
                  />
                  {/* A quiet icon, not a "Clear" word that re-prints itself down the column and
                      competes with the number it sits beside. */}
                  {on && (
                    <Tooltip label="Clear this minimum">
                      <IconButton
                        diameter={28} aria-label="Clear this minimum"
                        className="text-subtle hover:bg-surface-3 hover:text-danger-600"
                        onClick={() => setEdits((m) => ({ ...m, [p.productId]: null }))}
                      >
                        <X size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                </span>
              );
            },
          },
        ]}
      />
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        Only active products are shown. Changes apply to <b>{warehouse?.code}</b> only.
      </Typography.Paragraph>
    </Modal>
  );
}
